import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Upload } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast, Toaster } from "react-hot-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AddEditStudentModal from "@/components/mentorship/AddEditStudentModal";
import AddEditBatchModal from "@/components/mentorship/AddEditBatchModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getBatchesByCourse,
  getStudentsByCourse,
  addStudent,
  reassignStudent,
  createBatch,
  markStudentsAssigned,
  markBatchesAssigned,
  searchStudentsByCourse,
  deleteStudent as deleteStudentAPI
} from "@/services/mentorshipService";
import SubscriptionIdModal from '@/components/mentorship/SubscriptionIdModal';
import BatchesTable from "@/components/mentorship/BatchesTable";
import StudentsTable from "@/components/mentorship/StudentsTable";
import ScheduleMeetingModal from "@/components/mentorship/ScheduleMeetingModal";
import { Calendar } from "lucide-react";
import AddStudentsCSVModal, { BatchDeleteStudentsCSVModal } from "@/components/mentorship/AddStudentsCSVModal";

export default function CourseDetail() {
  const { id: courseName } = useParams(); // Using courseName as id
  const navigate = useNavigate();
  const [course, setCourse] = useState({ title: decodeURIComponent(courseName), id: courseName });
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batchSortConfig, setBatchSortConfig] = useState({ key: 'createdAt', direction: 'asc' });
  
  // Student filtering state
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  
  // Batch filtering state
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  
  // Student selection state
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  // Batch selection state
  const [selectedBatches, setSelectedBatches] = useState([]);
  
  // Modal states
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedBatchForEdit, setSelectedBatchForEdit] = useState(null);
  
  // Confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMarkAssignedConfirm, setShowMarkAssignedConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "student" or "batch"

  // Add state for subscription ID modal
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [selectedBatchForSubscription, setSelectedBatchForSubscription] = useState(null);

  // Add state for meeting scheduling modal
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);

  // Add state for marking batches as assigned
  const [showMarkBatchesAssignedConfirm, setShowMarkBatchesAssignedConfirm] = useState(false);

  // Add state for CSV upload modal
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);

  // 1. Add new state for batch assignment filter
  const [batchAssignmentFilter, setBatchAssignmentFilter] = useState('all');

  // Add state for search loading
  const [searchLoading, setSearchLoading] = useState(false);

  // Add new state for batch delete CSV modal
  const [showBatchDeleteCSVModal, setShowBatchDeleteCSVModal] = useState(false);

  // Load batches and students when component mounts
  useEffect(() => {
    fetchData();
  }, [courseName, assignmentFilter]);

  // Debounced search effect
  useEffect(() => {
    if (!studentSearchTerm) {
      // If search is cleared, reload default students
      fetchData();
      return;
    }
    // Debounce search
    const handler = setTimeout(async () => {
      setSearchLoading(true);
      setError(null);
      try {
        const response = await searchStudentsByCourse(courseName, studentSearchTerm);
        if (response && response.students) {
          const processedStudents = response.students.map(student => ({
            id: student.SK,
            name: student.name,
            email: student.email,
            phoneNo: student.phone,
            countryCode: student.countryCode || "91",
            courseName: courseName,
            batchName: student.batchId,
            courseId: student.courseId,
            courseUrl: student.courseUrl || "",
            isAssigned: student.isAssigned === true
          }));
          setStudents(processedStudents);
        }
      } catch (err) {
        setError("Failed to search students. Please try again.");
      } finally {
        setSearchLoading(false);
      }
    }, 400); // 400ms debounce
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSearchTerm, courseName]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedStudents([]); // Clear selected students when fetching new data
      
      // Determine assigned filter value for API
      let assignedApiValue;
      if (assignmentFilter === "assigned") {
        assignedApiValue = "assigned";
      } else if (assignmentFilter === "unassigned") {
        assignedApiValue = "unassigned";
      }
      
      // Fetch batches and students in parallel
      const [batchesResponse, studentsResponse] = await Promise.all([
        getBatchesByCourse(courseName),
        getStudentsByCourse(courseName, assignedApiValue)
      ]);
      
      // Process the batches data
      if (batchesResponse && batchesResponse.batchCounts) {
        setBatches(batchesResponse.batchCounts);
      }
      
      // Process the students data
      if (studentsResponse && studentsResponse.students) {
        // Transform students data to match our component structure
        const processedStudents = studentsResponse.students.map(student => ({
          id: student.SK, // Using SK as id
          name: student.name,
          email: student.email,
          phoneNo: student.phone,
          countryCode: student.countryCode || "91", // Default to 91 if not present
          courseName: courseName,
          batchName: student.batchId,
          courseId: student.courseId,
          courseUrl: student.courseUrl || "",
          isAssigned: student.isAssigned === true
        }));
        setStudents(processedStudents);
        
        // Update course info with total students count
        setCourse(prev => ({
          ...prev,
          totalStudents: processedStudents.length
        }));
      }
    } catch (err) {
      console.error("Error fetching course data:", err);
      setError("Failed to load course data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Sort batches
  const handleBatchSort = (key) => {
    setBatchSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Helper function to compare batch suffixes (both alphabetical and numerical)
  const compareBatchIds = (a, b) => {
    // Extract suffixes (assuming format like CourseName-A or CourseName-1)
    const getSuffix = (str) => {
      const parts = str.split('-');
      return parts[parts.length - 1];
    };
    
    const suffixA = getSuffix(a.SK);
    const suffixB = getSuffix(b.SK);
    
    // Check if both are numerical
    const isNumericA = /^\d+$/.test(suffixA);
    const isNumericB = /^\d+$/.test(suffixB);
    
    // If both are numerical, compare as numbers
    if (isNumericA && isNumericB) {
      return parseInt(suffixA) - parseInt(suffixB);
    }
    
    // If one is numeric and one is alphabetical, numeric comes first
    if (isNumericA && !isNumericB) {
      return -1;
    }
    
    if (!isNumericA && isNumericB) {
      return 1;
    }
    
    // If both are alphabetical
    // If suffixes have different lengths, shorter comes first (e.g., A before AA)
    if (suffixA.length !== suffixB.length) {
      return suffixA.length - suffixB.length;
    }
    
    // If same length, compare lexicographically
    return suffixA.localeCompare(suffixB);
  };

  const sortedBatches = [...batches]
    .sort((a, b) => {
      if (batchSortConfig.key === 'count' || batchSortConfig.key === 'capacity') {
        return batchSortConfig.direction === 'asc' 
          ? a[batchSortConfig.key] - b[batchSortConfig.key]
          : b[batchSortConfig.key] - a[batchSortConfig.key];
      } else if (batchSortConfig.key === 'SK') {
        // Use special alphabetical batch ID comparison for SK field
        return batchSortConfig.direction === 'asc'
          ? compareBatchIds(a, b)
          : compareBatchIds(b, a);
      } else {
        return batchSortConfig.direction === 'asc'
          ? String(a[batchSortConfig.key]).localeCompare(String(b[batchSortConfig.key]))
          : String(b[batchSortConfig.key]).localeCompare(String(a[batchSortConfig.key]));
      }
    })
    .filter(batch => {
      // Text search filter
      const textMatch = 
        String(batch.PK).toLowerCase().includes(batchSearchTerm.toLowerCase()) ||
        String(batch.SK).toLowerCase().includes(batchSearchTerm.toLowerCase()) ||
        (batch.courseId && String(batch.courseId).toLowerCase().includes(batchSearchTerm.toLowerCase()));
      
      return textMatch;
    });

  // Filter students based on search term, selected batch, and assignment status
  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      (student.name && student.name.toLowerCase().includes(studentSearchTerm.toLowerCase())) ||
      (student.email && student.email.toLowerCase().includes(studentSearchTerm.toLowerCase())) ||
      (student.phoneNo && String(student.phoneNo).includes(studentSearchTerm)) ||
      (student.courseId && student.courseId.toLowerCase().includes(studentSearchTerm.toLowerCase())) ||
      (student.batchName && student.batchName.toLowerCase().includes(studentSearchTerm.toLowerCase()));
    const matchesBatch = selectedBatch === "all" || !selectedBatch ? true : student.batchName === selectedBatch;
    return matchesSearch && matchesBatch;
  });

  const handleExportCSV = () => {
    // If there are selected students, only export those
    const studentsToExport = selectedStudents.length > 0 
      ? students.filter(student => selectedStudents.includes(student.id))
      : filteredStudents;
      
    // Check if any student belongs to a batch without courseId
    const batchesWithoutCourseId = new Set();
    const unassignedBatches = studentsToExport.filter(student => {
      const batch = batches.find(b => b.SK === student.batchName);
      const hasCourseId = batch && batch.courseId && batch.courseId.trim() !== "";
      if (!hasCourseId && batch) {
        batchesWithoutCourseId.add(student.batchName);
      }
      return !hasCourseId;
    });
    
    if (unassignedBatches.length > 0) {
      const batchNames = Array.from(batchesWithoutCourseId).join(", ");
      toast.error(`Cannot export. Some selected students belong to unassigned batches: ${batchNames}`);
      return;
    }
    
    // Format CSV as required: courseUrl,name,countryCode,mobile
    const headers = ["courseUrl", "name", "countryCode", "mobile"];
    const csvContent = [
      headers.join(","),
      ...studentsToExport.map(student => {
        const batch = batches.find(b => b.SK === student.batchName);
        const courseUrl = batch?.courseUrl || "https://classplusapp.com/diy/courses/course-overview?id=";
        const courseId = batch?.courseId || "";
        return [
          `${courseUrl}${courseId}`,
          student.name,
          student.countryCode || "91",
          student.phoneNo
        ].join(",");
      })
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `students_${courseName}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddStudent = () => {
    setSelectedStudent(null);
    setStudentModalOpen(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setStudentModalOpen(true);
  };

  const handleAddBatch = () => {
    setSelectedBatchForEdit(null);
    setBatchModalOpen(true);
  };

  const handleEditBatch = (batch) => {
    setSelectedBatchForEdit(batch);
    setBatchModalOpen(true);
  };

  // ✅ FIXED: Add proper modal close handlers
  const handleStudentModalClose = (open) => {
    if (!open) {
      setSelectedStudent(null);
    }
    setStudentModalOpen(open);
  };

  const handleBatchModalClose = (open) => {
    if (!open) {
      setSelectedBatchForEdit(null);
    }
    setBatchModalOpen(open);
  };

  const handleSubscriptionModalClose = (open) => {
    if (!open) {
      setSelectedBatchForSubscription(null);
    }
    setIsSubscriptionModalOpen(open);
  };

  const handleMeetingModalClose = (open) => {
    if (!open) {
      // Reset batch selection when meeting modal closes
      setSelectedBatches([]);
      
      // ✅ ADDED: Additional cleanup for meeting modal
      setTimeout(() => {
        // Ensure body is interactive
        document.body.removeAttribute('data-modal-open');
        document.body.style.overflow = 'auto';
        document.body.style.pointerEvents = 'auto';
        
        // Remove any trapped focus
        const activeElement = document.activeElement;
        if (activeElement && activeElement.closest('[role="dialog"]')) {
          document.body.focus();
        }
      }, 150);
    }
    setIsMeetingModalOpen(open);
  };

  const handleDeleteStudent = (student) => {
    setItemToDelete(student);
    setDeleteType("student");
    setShowDeleteConfirm(true);
  };

  const handleDeleteBatch = (batch) => {
    setItemToDelete(batch);
    setDeleteType("batch");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "student") {
      try {
        await deleteStudentAPI(itemToDelete.courseName || courseName, itemToDelete.batchName, itemToDelete.phoneNo);
        setStudents(students.filter(s => s.id !== itemToDelete.id));
        if (selectedStudents.includes(itemToDelete.id)) {
          setSelectedStudents(selectedStudents.filter(id => id !== itemToDelete.id));
        }
        toast.success("Student deleted successfully.");
      } catch (err) {
        toast.error("Failed to delete student. Please try again.");
      }
    } else if (deleteType === "batch") {
      // Check if batch has students
      const hasStudents = students.some(student => student.batchName === itemToDelete.SK);
      if (hasStudents) {
        toast.error("Cannot delete batch with students. Please reassign or delete students first.");
        setShowDeleteConfirm(false);
        return;
      }
      // TODO: Implement batch deletion API
      setBatches(batches.filter(b => b.SK !== itemToDelete.SK));
    }
    setShowDeleteConfirm(false);
  };

  const handleSaveStudent = async (studentData) => {
    try {
      setError(null);
      
      // Prepare data with courseIdValue if it exists
      const courseId = studentData.courseIdValue || studentData.courseId;
      
      if (selectedStudent) {
        // Edit existing student - use reassignStudent if batch changed
        if (selectedStudent.batchName !== studentData.batchName) {
          const response = await reassignStudent({
            courseName: courseName,
            phone: studentData.phoneNo,
            newBatchId: studentData.batchName
          });
          
          if (response && response.student) {
            // Update student in the list with the response data
            setStudents(students.map(s => s.id === selectedStudent.id ? {
              ...s,
              batchName: response.student.batchId,
              // Update other fields if needed
              name: studentData.name,
              email: studentData.email,
              phoneNo: studentData.phoneNo,
              courseUrl: studentData.courseUrl,
              courseId: courseId
            } : s));
            
            // Update batch counts
            const oldBatch = batches.find(b => b.SK === selectedStudent.batchName);
            const newBatch = batches.find(b => b.SK === studentData.batchName);
            
            if (oldBatch && newBatch) {
              setBatches(batches.map(b => {
                if (b.SK === selectedStudent.batchName) {
                  return { ...b, count: Math.max(0, b.count - 1) };
                } else if (b.SK === studentData.batchName) {
                  return { ...b, count: b.count + 1 };
                }
                return b;
              }));
            }
          }
        } else {
          // Just update locally if batch didn't change (no API for updating other fields)
          setStudents(students.map(s => s.id === selectedStudent.id ? {
            ...s,
            name: studentData.name,
            email: studentData.email,
            phoneNo: studentData.phoneNo,
            courseUrl: studentData.courseUrl,
            courseId: courseId
          } : s));
        }
      } else {
        // Add new student
        const response = await addStudent({
          name: studentData.name,
          email: studentData.email,
          courseName: courseName,
          phone: studentData.phoneNo,
          courseId: courseId
        });
        
        if (response && response.student) {
          const newStudent = {
            id: `${response.student.batchId}#${response.student.phone}`,
            name: response.student.name,
            email: response.student.email,
            phoneNo: response.student.phone,
            countryCode: "91", // Default
            courseName: response.student.courseName,
            batchName: response.student.batchId,
            courseId: courseId,
            courseUrl: studentData.courseUrl || "https://classplusapp.com/diy/courses/course-overview?id=",
            isAssigned: false // New students are unassigned by default
          };
          
          setStudents([...students, newStudent]);
          
          // Update the batch count
          const updatedBatch = batches.find(b => b.SK === response.student.batchId);
          if (updatedBatch) {
            setBatches(batches.map(b => 
              b.SK === response.student.batchId 
                ? { ...b, count: b.count + 1 } 
                : b
            ));
          }
        }
      }
    } catch (err) {
      console.error("Error saving student:", err);
      setError("Failed to save student. Please try again.");
    }
  };

  const handleSaveBatch = async (batchData) => {
    try {
      setError(null);
      
      if (selectedBatchForEdit) {
        // Edit existing batch - still handled locally
        // Currently no API endpoint to modify batches
        setBatches(batches.map(b => b.SK === batchData.SK ? {
          ...b,
          capacity: batchData.capacity,
          count: batchData.count,
          courseId: batchData.courseId,
          courseUrl: batchData.courseUrl
        } : b));
      } else {
        // Add new batch - use the API
        const response = await createBatch({
          PK: courseName,
          SK: batchData.SK,
          capacity: batchData.capacity,
          count: 0,
          courseId: batchData.courseId,
          courseUrl: batchData.courseUrl
        });
        
        if (response && response.course) {
          // Add the new batch to the list
          const newBatch = {
            PK: response.course.courseName,
            SK: response.course.batchName,
            capacity: response.course.capacity,
            count: response.course.currentCount || 0,
            courseId: response.course.courseId,
            courseUrl: response.course.courseUrl,
            createdAt: new Date().toISOString()
          };
          
          setBatches([...batches, newBatch]);
        }
      }
    } catch (err) {
      console.error("Error saving batch:", err);
      setError("Failed to save batch. Please try again.");
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAllStudents = (isChecked) => {
    if (isChecked) {
      setSelectedStudents(filteredStudents.map(s => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleMarkAsAssigned = async () => {
    try {
      if (selectedStudents.length === 0) {
        toast.error("No students selected");
        return;
      }
      
      // Close the confirmation modal
      setShowMarkAssignedConfirm(false);
      
      // Call the API to mark students as assigned
      const response = await markStudentsAssigned(courseName, selectedStudents);
      
      if (response && response.message) {
        // Update the local state to reflect the change
        setStudents(students.map(student => 
          selectedStudents.includes(student.id) 
            ? { ...student, isAssigned: true }
            : student
        ));
        
        // Clear selected students
        setSelectedStudents([]);
        
        // Show success message
        toast.success("Selected students marked as assigned successfully.");
        
        // Refresh data if necessary
        if (assignmentFilter === "unassigned") {
          fetchData();
        }
      }
    } catch (error) {
      console.error("Error marking students as assigned:", error);
      toast.error("Failed to mark students as assigned.");
    }
  };

  const openSubscriptionModal = (batch) => {
    setSelectedBatchForSubscription(batch);
    setIsSubscriptionModalOpen(true);
  };

  const handleBatchSelection = (batchId, checked) => {
    setSelectedBatches(prev => {
      if (checked) {
        return [...prev, batchId];
      } else {
        return prev.filter(id => id !== batchId);
      }
    });
  };

  const handleSelectAllBatches = (checked) => {
    if (checked) {
      setSelectedBatches(sortedBatches.map(batch => batch.SK));
    } else {
      setSelectedBatches([]);
    }
  };

  const handleMarkBatchesAsAssigned = async () => {
    try {
      if (selectedBatches.length === 0) {
        toast.error("No batches selected");
        return;
      }
      
      // Close the confirmation modal
      setShowMarkBatchesAssignedConfirm(false);
      
      // Call the API to mark batches as assigned
      const result = await markBatchesAssigned(courseName, selectedBatches);
      
      // Clear selected batches
      setSelectedBatches([]);
      
      // Show success message
      if (result.successfulCount > 0) {
        toast.success(`Successfully marked ${result.successfulCount} batch(es) as assigned.`);
      }
      
      if (result.failedCount > 0) {
        toast.warning(`${result.failedCount} batch(es) failed to be marked as assigned.`);
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error marking batches as assigned:", error);
      toast.error("Failed to mark batches as assigned.");
    }
  };

  // ✅ FIXED: Add cleanup for modal states
  const resetModalStates = () => {
    setStudentModalOpen(false);
    setBatchModalOpen(false);
    setSelectedStudent(null);
    setSelectedBatchForEdit(null);
    setShowDeleteConfirm(false);
    setShowMarkAssignedConfirm(false);
    setItemToDelete(null);
    setDeleteType("");
    setIsSubscriptionModalOpen(false);
    setSelectedBatchForSubscription(null);
    setIsMeetingModalOpen(false);
    setShowMarkBatchesAssignedConfirm(false);
  };

  // ✅ FIXED: Add cleanup on unmount
  useEffect(() => {
    return () => {
      resetModalStates();
    };
  }, []);

  // 3. Filter batches before rendering BatchesTable
  const filteredBatches = batches.filter(batch => {
    if (batchAssignmentFilter === 'assigned') {
      return batch.batchStatus === 'assigned';
    } else if (batchAssignmentFilter === 'unassigned') {
      return batch.batchStatus === 'unassigned';
    }
    return true; // 'all'
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">{course.title}</h1>
          <p className="text-gray-500">
            Total Students: {course.totalStudents || students.length}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/mentorship")}>
          Back to Dashboard
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="students" className="w-full">
        <TabsList className="grid grid-cols-2 max-w-md mb-8">
          <TabsTrigger value="batches">Batches</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
        </TabsList>

        <TabsContent value="batches">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Batches</CardTitle>
              <div className="flex gap-2">
                <div className="flex w-full max-w-sm items-center space-x-2">
                  <Input 
                    placeholder="Search batches..." 
                    value={batchSearchTerm}
                    onChange={(e) => setBatchSearchTerm(e.target.value)}
                  />
                  <div className="flex items-center space-x-2">
                    <Select value={batchAssignmentFilter} onValueChange={setBatchAssignmentFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Batch Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Actions</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setIsMeetingModalOpen(true)}>
                      <Calendar className="mr-2 h-4 w-4" />
                      Schedule Meeting
                    </DropdownMenuItem>
                    {selectedBatches.length > 0 && (
                      <DropdownMenuItem onClick={() => setShowMarkBatchesAssignedConfirm(true)}>
                        <Check className="mr-2 h-4 w-4" />
                        Mark as Assigned
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={handleAddBatch}>Add New Batch</Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedBatches.length > 0 && (
                <div className="flex justify-between items-center mb-4 p-2 bg-slate-100 rounded">
                  <span>{selectedBatches.length} batch(es) selected</span>
                </div>
              )}
              <BatchesTable
                batches={filteredBatches}
                batchSortConfig={batchSortConfig}
                onBatchSort={handleBatchSort}
                onEditBatch={handleEditBatch}
                onDeleteBatch={handleDeleteBatch}
                onOpenSubscriptionModal={openSubscriptionModal}
                selectedBatches={selectedBatches}
                onBatchSelection={handleBatchSelection}
                onSelectAllBatches={handleSelectAllBatches}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="students">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Students</CardTitle>
              <div className="flex gap-2">
                <div className="flex w-full max-w-sm items-center space-x-2">
                  <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Assignment Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Students</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    placeholder="Search students..." 
                    value={studentSearchTerm}
                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                    disabled={searchLoading}
                  />
                  <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by batch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Batches</SelectItem>
                      {batches.map(batch => (
                        <SelectItem key={batch.SK} value={batch.SK || "unassigned"}>
                          {batch.SK} {!batch.courseId ? "(Unassigned)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Actions</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setShowCSVUploadModal(true)}>
                      <Upload className="mr-2 h-4 w-4" />
                      Add Students via CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowBatchDeleteCSVModal(true)}>
                      <Upload className="mr-2 h-4 w-4 text-red-600" />
                      Delete Students via CSV
                    </DropdownMenuItem>
                    {selectedStudents.length > 0 && (
                      <DropdownMenuItem onClick={() => setShowMarkAssignedConfirm(true)}>
                        <Check className="mr-2 h-4 w-4" />
                        Mark Students as Assigned ({selectedStudents.length})
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={handleExportCSV}>Export to CSV</Button>
                <Button onClick={handleAddStudent}>Add New Student</Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedStudents.length > 0 && (
                <div className="flex justify-between items-center mb-4 p-2 bg-slate-100 rounded">
                  <span>{selectedStudents.length} student(s) selected</span>
                </div>
              )}
              <StudentsTable
                students={filteredStudents}
                batches={batches}
                selectedStudents={selectedStudents}
                onStudentSelection={handleStudentSelection}
                onSelectAllStudents={handleSelectAllStudents}
                onEditStudent={handleEditStudent}
                onDeleteStudent={handleDeleteStudent}
              />
              {searchLoading && <div className="text-center text-gray-500 mt-2">Searching...</div>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Student Modal */}
      <AddEditStudentModal
        open={studentModalOpen}
        onOpenChange={handleStudentModalClose}
        student={selectedStudent}
        batches={batches}
        courses={[course].filter(Boolean)}
        onSave={handleSaveStudent}
      />

      {/* Batch Modal */}
      {course && (
        <AddEditBatchModal
          open={batchModalOpen}
          onOpenChange={handleBatchModalClose}
          batch={selectedBatchForEdit}
          onSave={handleSaveBatch}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === "student" 
                ? "This will permanently delete this student's record." 
                : "This will permanently delete this batch. This action cannot be undone if there are students in this batch."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Assigned Confirmation Dialog */}
      <AlertDialog open={showMarkAssignedConfirm} onOpenChange={setShowMarkAssignedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark {selectedStudents.length} selected student{selectedStudents.length !== 1 ? 's' : ''} as assigned?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkAsAssigned}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subscription ID Modal */}
      <SubscriptionIdModal
        open={isSubscriptionModalOpen}
        onOpenChange={handleSubscriptionModalClose}
        batch={selectedBatchForSubscription}
      />

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        open={isMeetingModalOpen}
        onOpenChange={handleMeetingModalClose}
        selectedBatches={selectedBatches}
        courseName={decodeURIComponent(courseName)}
      />

      {/* Mark Batches as Assigned Confirmation Dialog */}
      <AlertDialog open={showMarkBatchesAssignedConfirm} onOpenChange={setShowMarkBatchesAssignedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark the selected batches as assigned?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkBatchesAsAssigned}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Students CSV Modal */}
      <AddStudentsCSVModal
        open={showCSVUploadModal}
        onOpenChange={setShowCSVUploadModal}
        courseName={decodeURIComponent(courseName)}
        onSuccess={fetchData}
      />

      {/* Batch Delete Students CSV Modal */}
      <BatchDeleteStudentsCSVModal
        open={showBatchDeleteCSVModal}
        onOpenChange={setShowBatchDeleteCSVModal}
        courseName={decodeURIComponent(courseName)}
        onSuccess={fetchData}
      />
    </div>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>
      
      <div className="mb-8">
        <Skeleton className="h-10 w-64 mb-6" />
      </div>
      
      <div className="space-y-6">
        <Skeleton className="h-96 w-full" />
      </div>
    </div>
  );
} 